/**
 * Sprint Management Hook
 *
 * Provides state and operations for sprint planning and tracking.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  type Sprint,
  type SprintSummary,
  getSprintSummaries,
  createSprint,
  updateSprint,
  deleteSprint,
  startSprint,
  completeSprint,
  assignTaskToSprint,
  bulkAssignTasksToSprint,
  type CreateSprintData,
  type UpdateSprintData,
} from '@/db/sprints';
import { useDatabase } from '@/context/DatabaseContext';
import type { Task } from '@/types';
import { isCompletedStatus } from '@/constants/taskStatuses';

export interface SprintVelocity {
  sprintId: string;
  sprintName: string;
  completedPoints: number;
  plannedPoints: number;
  completedTasks: number;
  totalTasks: number;
}

export interface BurndownDataPoint {
  date: number;
  remainingPoints: number;
  idealRemaining: number;
  actualRemaining: number;
}

interface UseSprintsOptions {
  spaceId: string | null;
  autoFetch?: boolean;
}

interface UseSprintsReturn {
  // State
  sprints: SprintSummary[];
  selectedSprint: SprintSummary | null;
  loading: boolean;
  error: string | null;

  // Sprint selection
  setSelectedSprintId: (id: string | null) => void;

  // CRUD operations
  createNewSprint: (data: CreateSprintData) => Promise<Sprint>;
  updateSprintData: (id: string, data: UpdateSprintData) => Promise<Sprint>;
  removeSpprint: (id: string) => Promise<boolean>;
  startSprintById: (id: string) => Promise<Sprint>;
  completeSprintById: (id: string) => Promise<Sprint>;

  // Task assignment
  assignTask: (taskId: string, sprintId: string | null) => Promise<void>;
  bulkAssignTasks: (taskIds: string[], sprintId: string | null) => Promise<void>;

  // Derived data
  backlogTasks: Task[];
  sprintTasks: Task[];
  doneTasks: Task[];
  velocityData: SprintVelocity[];
  burndownData: BurndownDataPoint[];

  // Helpers
  getSprintProgress: (sprint: SprintSummary) => number;
  getSprintDaysRemaining: (sprint: Sprint) => number;
  refresh: () => Promise<void>;
}

export function useSprints({ spaceId, autoFetch = true }: UseSprintsOptions): UseSprintsReturn {
  const { tasks, updateTask } = useDatabase();
  const [sprints, setSprints] = useState<SprintSummary[]>([]);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch sprints when spaceId changes
  const fetchSprints = useCallback(async () => {
    if (!spaceId) {
      setSprints([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getSprintSummaries(spaceId);
      setSprints(data);

      // Auto-select active sprint if none selected
      if (!selectedSprintId) {
        const activeSprint = data.find((s) => s.status === 'active');
        if (activeSprint) {
          setSelectedSprintId(activeSprint.id);
        } else if (data.length > 0) {
          // Select most recent planned sprint
          const plannedSprints = data.filter((s) => s.status === 'planned');
          if (plannedSprints.length > 0) {
            setSelectedSprintId(plannedSprints[plannedSprints.length - 1].id);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sprints');
    } finally {
      setLoading(false);
    }
  }, [spaceId, selectedSprintId]);

  useEffect(() => {
    if (autoFetch) {
      fetchSprints();
    }
  }, [fetchSprints, autoFetch]);

  // Selected sprint object
  const selectedSprint = useMemo(() => {
    return sprints.find((s) => s.id === selectedSprintId) || null;
  }, [sprints, selectedSprintId]);

  // Filter tasks by sprint
  const spaceTasks = useMemo(() => {
    if (!spaceId) return [];
    return tasks.filter((t) => t.spaceId === spaceId);
  }, [tasks, spaceId]);

  // Backlog: tasks without sprint assignment
  const backlogTasks = useMemo(() => {
    return spaceTasks.filter((t) => !t.sprintId && !isCompletedStatus(t.status));
  }, [spaceTasks]);

  // Sprint tasks: tasks in the selected sprint that are not completed
  const sprintTasks = useMemo(() => {
    if (!selectedSprintId) return [];
    return spaceTasks.filter(
      (t) => t.sprintId === selectedSprintId && !isCompletedStatus(t.status)
    );
  }, [spaceTasks, selectedSprintId]);

  // Done tasks: completed tasks in the selected sprint
  const doneTasks = useMemo(() => {
    if (!selectedSprintId) return [];
    return spaceTasks.filter(
      (t) => t.sprintId === selectedSprintId && isCompletedStatus(t.status)
    );
  }, [spaceTasks, selectedSprintId]);

  // Velocity data for the last 5 completed sprints
  const velocityData = useMemo((): SprintVelocity[] => {
    const completedSprints = sprints
      .filter((s) => s.status === 'completed')
      .slice(-5);

    return completedSprints.map((sprint) => ({
      sprintId: sprint.id,
      sprintName: sprint.name,
      completedPoints: sprint.completedStoryPoints,
      plannedPoints: sprint.totalStoryPoints,
      completedTasks: sprint.completedTasks,
      totalTasks: sprint.totalTasks,
    }));
  }, [sprints]);

  // Burndown data for selected sprint
  const burndownData = useMemo((): BurndownDataPoint[] => {
    if (!selectedSprint || !selectedSprint.startDate || !selectedSprint.endDate) {
      return [];
    }

    const startDate = selectedSprint.startDate;
    const endDate = selectedSprint.endDate;
    const totalPoints = selectedSprint.totalStoryPoints;
    const durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

    if (durationDays <= 0) return [];

    const data: BurndownDataPoint[] = [];
    const today = Date.now();

    // Group completed tasks by day
    const completedByDay = new Map<number, number>();
    const sprintTasksAll = spaceTasks.filter((t) => t.sprintId === selectedSprintId);

    sprintTasksAll.forEach((task) => {
      if (isCompletedStatus(task.status)) {
        // Use updatedAt as completion date approximation
        const completionDate = new Date(task.updatedAt).getTime();
        if (completionDate >= startDate && completionDate <= endDate) {
          const dayIndex = Math.floor((completionDate - startDate) / (1000 * 60 * 60 * 24));
          const points = task.storyPoints || 0;
          completedByDay.set(dayIndex, (completedByDay.get(dayIndex) || 0) + points);
        }
      }
    });

    let cumulativeCompleted = 0;

    for (let day = 0; day <= durationDays; day++) {
      const date = startDate + day * 24 * 60 * 60 * 1000;
      const idealRemaining = totalPoints - (totalPoints / durationDays) * day;

      cumulativeCompleted += completedByDay.get(day) || 0;
      const actualRemaining = totalPoints - cumulativeCompleted;

      // Only add actual data up to today
      if (date <= today) {
        data.push({
          date,
          remainingPoints: actualRemaining,
          idealRemaining: Math.max(0, idealRemaining),
          actualRemaining,
        });
      } else {
        // Future dates only show ideal line
        data.push({
          date,
          remainingPoints: actualRemaining,
          idealRemaining: Math.max(0, idealRemaining),
          actualRemaining: -1, // Indicator for future
        });
      }
    }

    return data;
  }, [selectedSprint, selectedSprintId, spaceTasks]);

  // CRUD operations
  const createNewSprint = useCallback(
    async (data: CreateSprintData): Promise<Sprint> => {
      const sprint = await createSprint(data);
      await fetchSprints();
      return sprint;
    },
    [fetchSprints]
  );

  const updateSprintData = useCallback(
    async (id: string, data: UpdateSprintData): Promise<Sprint> => {
      const sprint = await updateSprint(id, data);
      await fetchSprints();
      return sprint;
    },
    [fetchSprints]
  );

  const removeSpprint = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await deleteSprint(id);
      if (selectedSprintId === id) {
        setSelectedSprintId(null);
      }
      await fetchSprints();
      return result;
    },
    [fetchSprints, selectedSprintId]
  );

  const startSprintById = useCallback(
    async (id: string): Promise<Sprint> => {
      const sprint = await startSprint(id);
      await fetchSprints();
      return sprint;
    },
    [fetchSprints]
  );

  const completeSprintById = useCallback(
    async (id: string): Promise<Sprint> => {
      const sprint = await completeSprint(id);
      await fetchSprints();
      return sprint;
    },
    [fetchSprints]
  );

  // Task assignment with local state update
  const assignTask = useCallback(
    async (taskId: string, sprintId: string | null): Promise<void> => {
      await assignTaskToSprint(taskId, sprintId);
      // Update local task state
      updateTask(taskId, { sprintId });
      await fetchSprints(); // Refresh summaries
    },
    [updateTask, fetchSprints]
  );

  const bulkAssignTasks = useCallback(
    async (taskIds: string[], sprintId: string | null): Promise<void> => {
      await bulkAssignTasksToSprint(taskIds, sprintId);
      // Update local task states
      taskIds.forEach((id) => updateTask(id, { sprintId }));
      await fetchSprints(); // Refresh summaries
    },
    [updateTask, fetchSprints]
  );

  // Helper functions
  const getSprintProgress = useCallback((sprint: SprintSummary): number => {
    if (sprint.totalTasks === 0) return 0;
    return Math.round((sprint.completedTasks / sprint.totalTasks) * 100);
  }, []);

  const getSprintDaysRemaining = useCallback((sprint: Sprint): number => {
    if (!sprint.endDate) return -1;
    const now = Date.now();
    const remaining = sprint.endDate - now;
    return Math.max(0, Math.ceil(remaining / (1000 * 60 * 60 * 24)));
  }, []);

  return {
    // State
    sprints,
    selectedSprint,
    loading,
    error,

    // Sprint selection
    setSelectedSprintId,

    // CRUD operations
    createNewSprint,
    updateSprintData,
    removeSpprint,
    startSprintById,
    completeSprintById,

    // Task assignment
    assignTask,
    bulkAssignTasks,

    // Derived data
    backlogTasks,
    sprintTasks,
    doneTasks,
    velocityData,
    burndownData,

    // Helpers
    getSprintProgress,
    getSprintDaysRemaining,
    refresh: fetchSprints,
  };
}
